import { useOidcContext } from '@uag/react-core';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { isLoginEnabled } from './authMode';
import { hasUserChosenLocale, normalizeLocale } from './catalog/locale';
import { catalogLocaleAtom } from './catalog/state';

// Preselects the GUI language from the logged-in user's own preference, once, if the user hasn't
// picked one in this browser yet (see chat: "Bekommen wir über das Login Objekt von DOKA auch die
// präferierte Sprache des Benutzers mit und können die Sprache vorauswählen?").
//
// What we can read from the token, in order of preference - NONE of these is guaranteed to be
// there, which is why every one is tried and a miss is silent:
//   - `locale`             - the standard OIDC claim. Entra ID does NOT emit it by default; it has
//                            to be added as an optional claim / via a claims-mapping policy.
//   - `ui_locales`         - standard OIDC, space-separated list of preferred languages.
//   - `xms_pl` / `xms_tpl` - Entra ID's own optional claims for the USER's preferred language and
//                            the TENANT's default language ("de-DE", "en-US"), both opt-in per app
//                            registration (Token configuration -> Add optional claim).
//   - `preferred_language` - non-standard, seen on some IdPs/gateways.
// If DOKA switches none of them on, `preferredLanguage` stays undefined and the browser language
// decides (see locale.ts initialLocale) - and the user's own choice always wins over both, since
// this never overwrites a stored one. The authoritative source, should the claims stay off, is
// Microsoft Graph's `/me` `preferredLanguage` - that would be a separate call, not this.
interface OidcUser {
    locale?: string;
    ui_locales?: string;
    xms_pl?: string;
    xms_tpl?: string;
    preferred_language?: string;
}

// Structural, all-optional shape - same convention as UserGreeting's own claim lookup: this sandbox
// has no access to the private @uag/react-core package to confirm useOidcContext()'s exact `user`
// type, and every claim below is optional anyway.
const preferredLanguage = (user: OidcUser | undefined) =>
    normalizeLocale(user?.locale) ??
    normalizeLocale(user?.ui_locales) ??
    normalizeLocale(user?.xms_pl) ??
    normalizeLocale(user?.preferred_language) ??
    normalizeLocale(user?.xms_tpl);

const UserLocaleSyncContent = () => {
    const { user } = useOidcContext();
    const setLocale = useSetAtom(catalogLocaleAtom);

    useEffect(() => {
        // An explicit choice from an earlier visit outranks the token - the user overruled their
        // own profile setting on purpose, and a login must not silently undo that.
        if (hasUserChosenLocale()) {
            return;
        }
        const fromToken = preferredLanguage(user);
        if (fromToken) {
            setLocale(fromToken);
        }
    }, [user, setLocale]);

    return null;
};

/** Mounted once (Frame) - and only where a real OIDC session can exist: useOidcContext() throws
 * without an AuthProvider ancestor, which is skipped whenever login is off (see authMode.ts), so
 * the hook-calling part must never be MOUNTED in that case, not just render nothing. */
export const UserLocaleSync = () => (isLoginEnabled ? <UserLocaleSyncContent /> : null);
